import React from 'react';
import { flow } from 'lodash';
import {
  PricesSectionType,
  PricesAccordionSection,
  PricesAccordionTab,
  PricesTabType,
} from 'store/state/selectors/insights/prices';
import { TransportationAccordionWrapper, SectionBody, AccordionTabsHeader } from '../../../transportation/styled';
import { SectionWrapper, SectionType } from 'components/widget/common/styled';
import { InsightType } from 'utils/entities';
import { Section } from 'components/filters/styled/common';
import { Title } from '../../Title';
import { Tabs } from 'ds/components/tabs';
import { ShowAt } from 'consts/breakpoints';
import { SmallText, Text } from 'ds/components/typography';
import { useLocale } from 'locale';
import PricesTabs from './prices-tabs';
import { PricesTabsBodyWrapper } from '../../styled';
import { connect } from 'react-redux';
import { RootAction, State } from 'store/state';
import { detailPageCategorySelector } from 'store/state/selectors/analytics';
import { DetailPageEventCategory } from 'analytics/handlers/detailPageSectionHandlers';
import { getTabTotalItemsCount, analyticsMap } from 'components/widget/prices/utils';
import { isConnectedUserSelector, unitPageBulletinSelector } from 'store/state/domainData/selectors';
import withEvents from 'analytics/withEvents';
import { Dispatch } from 'redux';
import { AuthenticationModalPayload, AuthenticationModalType } from 'components/authentication/types';
import { AuthModalSource } from 'store/state/app';
import { setAuthModal } from 'store/state/app/actions';

interface PricesAccordionProps {
  sections: PricesAccordionSection[];
  activeSection: PricesSectionType;
  setActiveSection: (section: PricesSectionType) => void;
  activeTab: PricesTabType;
  setActiveTab: (tab: PricesTabType) => void;
  eventCategory: DetailPageEventCategory;
  currentBulletinId?: string;
  isConnectedUser: boolean;
  onSetAuthModal: typeof setAuthModal;
}

const PricesAccordion: React.FC<PricesAccordionProps> = ({
  sections,
  activeSection,
  setActiveSection,
  activeTab,
  setActiveTab,
  currentBulletinId,
  isConnectedUser,
  onSetAuthModal,
}) => {
  const { t } = useLocale();

  const makeTabsRenderer = (sectionId: PricesSectionType) => (tab: PricesAccordionTab): JSX.Element => {
    return (
      <PricesTabs
        tab={tab}
        sectionId={sectionId}
      />
    );
  };

  return (
    <TransportationAccordionWrapper>
      {sections.map(section => (
        <SectionWrapper
          type={InsightType.Prices}
          className={activeSection === section.sectionId ? 'section-wrapper-active' : ''}
          key={section.sectionId}
        >
          <Section isOpen={activeSection === section.sectionId}>
            <Title
              sectionId={section.sectionId}
              isOpen={activeSection === section.sectionId}
              isActive={section.sectionId === activeSection}
              toggleActiveSection={() => setActiveSection(section.sectionId)}
            />
            <SectionBody type={InsightType.Prices} style={{ padding: '0 16px', margin: '0 -16px' }}>
              <div style={{ paddingTop: '8px' }}>
                <Tabs
                  activeTab={section.sectionId === activeSection ? section.tabs.findIndex(tab => tab.tabId === activeTab) : null}
                  setActiveTab={(idx: number) => {
                    if (!isConnectedUser && idx !== 0) {
                      onSetAuthModal({ type: AuthenticationModalType.GatedDeals }, { source: AuthModalSource.DealsGateTab });
                    }
                    else {
                      setActiveTab(section.tabs[idx].tabId);
                    }
                  }}
                  headerWrapper={AccordionTabsHeader}
                  headers={section.tabs.map((tab: PricesAccordionTab) => (
                    <SectionType
                      style={{ minWidth: 'auto' }}
                      key={tab.tabId}
                      isActive={section.sectionId === activeSection && tab.tabId === activeTab}
                    >
                      <ShowAt from={2}>
                        <Text>
                          {t('widget.prices.accordionTabTitle', {
                            type: tab.tabId,
                            amount: getTabTotalItemsCount(tab, currentBulletinId),
                          })}
                        </Text>
                      </ShowAt>
                      <ShowAt to={1}>
                        <SmallText>
                          {t('widget.prices.accordionTabTitle', {
                            type: tab.tabId,
                            amount: getTabTotalItemsCount(tab, currentBulletinId),
                          })}
                        </SmallText>
                      </ShowAt>
                    </SectionType>
                  ))}
                  bodyWrapper={PricesTabsBodyWrapper}
                  bodies={section.tabs.map(makeTabsRenderer(section.sectionId))}
                />
              </div>
            </SectionBody>
          </Section>
        </SectionWrapper>
      ))}
    </TransportationAccordionWrapper>
  );
};

const EventfulPricesAccordion = withEvents<PricesAccordionProps>((sendEvent, props) => ({
  setActiveSection(section) {
    sendEvent(`${props.eventCategory}_widget_section_click`, props.eventCategory, {
      event: {
        [`${props.eventCategory}_page_sections`]: 'prices',
        'widget_sections': `prices/${analyticsMap[section]}`,
      },
    });
  },
}))(PricesAccordion);

const bulletinIdSelector = flow(unitPageBulletinSelector, u => u && u.id);

const mapStateToProps = (state: State) => ({
  eventCategory: detailPageCategorySelector(state),
  currentBulletinId: bulletinIdSelector(state),
  isConnectedUser: isConnectedUserSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  onSetAuthModal: (payload: AuthenticationModalPayload, meta: { isUserInitiated?: boolean, source?: AuthModalSource }) => dispatch(setAuthModal(payload, meta)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EventfulPricesAccordion);
